import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Tooltip } from '@neo/primitives/tooltip'
import type { JsonSchemaProperty } from '@shared/types'
import { AlertCircle } from 'lucide-react'
import type { RExternalAppDetails } from 'v2/types/api/settings'
import { z } from 'zod'
import {
  buildNestedFieldValidation,
  flattenSchemaDefaults,
  getOptionsFromEnum,
} from './integration-form-utils'

export interface IntegrationFormValues {
  [key: string]: unknown
}

const fieldLabel = (label: string, description?: string) => (
  <Tooltip align="start" className="max-w-[370px] break-words" content={description}>
    <div className="flex items-center gap-2">
      <span className="text-s-500">{label}</span>
      <AlertCircle size={16} />
    </div>
  </Tooltip>
)

const resolveComponentType = (property: JsonSchemaProperty) => {
  if ('enum' in property) return 'select'
  if (property.secret) return 'inputPassword'
  return 'input'
}

const fieldConfig = (
  name: string,
  label: string,
  property: JsonSchemaProperty,
  validation: z.ZodTypeAny,
): GenericFieldConfig<IntegrationFormValues>[number] => {
  const testId = `input-integration-${name.replaceAll('__', '-')}`
  const isEnum = 'enum' in property

  return {
    name,
    componentType: resolveComponentType(property),
    label: fieldLabel(label, property.description),
    inputProps: isEnum
      ? { placeholder: 'Select', options: getOptionsFromEnum(property.enum), 'data-testid': testId }
      : { placeholder: `Enter ${label}`, 'data-testid': testId },
    zodValidation: validation,
  }
}

export const buildIntegrationConnectionFormConfig = (
  app: RExternalAppDetails,
  aciEnabled = false,
) => {
  const properties = app.input_schema?.properties ?? {}
  const validations: Record<string, z.ZodTypeAny> = {}
  const fields: GenericFieldConfig<IntegrationFormValues> = []

  const addNestedFields = (category: string, categoryProperty: JsonSchemaProperty) => {
    // `JsonSchemaProperty.required` is declared boolean; only a JSON Schema
    // object node carries the field-name list this branch wants.
    const requiredList: unknown = categoryProperty.required
    const explicitlyRequired = Array.isArray(requiredList) ? (requiredList as string[]) : undefined
    Object.entries(categoryProperty.properties ?? {}).forEach(([name, property]) => {
      if (property.hidden) return
      const key = `${category}__${name}`
      const label = property.title || name.replaceAll('_', ' ')
      // The schema's `required` list is only honoured on the new service; the
      // legacy form always treated every visible field as required.
      const isRequired = aciEnabled && explicitlyRequired ? explicitlyRequired.includes(name) : true
      const validation = buildNestedFieldValidation(category, label, isRequired, aciEnabled)
      validations[key] = validation
      fields.push(fieldConfig(key, label, property, validation))
    })
  }

  if (properties.connection) addNestedFields('connection', properties.connection)
  if (properties.credentials) addNestedFields('credentials', properties.credentials)

  const topLevelRequired = app.input_schema?.required as string[] | undefined
  Object.entries(properties).forEach(([name, property]) => {
    if (name === 'connection' || name === 'credentials' || property.hidden || !property.type) return
    const label = property.title || name.replaceAll('_', ' ')
    const validation = buildNestedFieldValidation(
      'connection',
      label,
      aciEnabled && topLevelRequired ? topLevelRequired.includes(name) : true,
      aciEnabled,
    )
    validations[name] = validation
    fields.push(fieldConfig(name, label, property, validation))
  })

  return {
    fields,
    schema: z.object(validations),
    defaults: flattenSchemaDefaults(app.input_schema),
  }
}
